import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EDU_BASENAME, getAppBasename } from '../../config/appBase';
import { OPTIONAL } from './consentCatalogue';
import { EssentialTable, ProviderTables } from './ConsentTables';
import {
  CONSENT_CHANGE_EVENT,
  OPEN_SETTINGS_EVENT,
  clearSettingsOpener,
  getChoice,
  getSettingsOpener,
  saveChoice,
} from './consentStore';
import './CookieConsent.css';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
const ALL_ON = { analytics: true, recording: true, advertising: true };
const ALL_OFF = { analytics: false, recording: false, advertising: false };

function isJoinPath(pathname) {
  return pathname === '/join' || pathname.indexOf('/join/') === 0;
}

function PolicyLink({ onClick }) {
  const { pathname } = useLocation();
  // The company home is a separate page load with no router basename, so a
  // router Link would drop the /edu prefix the cookie policy lives under.
  if (getAppBasename() === '' && pathname === '/about') {
    return <a href={`${EDU_BASENAME}/cookies`} onClick={onClick}>Read our Cookie Policy</a>;
  }
  return <Link to="/cookies" onClick={onClick}>Read our Cookie Policy</Link>;
}

/**
 * The cookie banner (first layer) and the cookie settings panel (second
 * layer). Mounted once, inside the router and theme. Nothing optional loads
 * until the visitor chooses; ignoring or closing means no consent.
 */
export default function CookieConsent() {
  const { pathname } = useLocation();
  const [, setTick] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [draft, setDraft] = useState(ALL_OFF);
  const bannerRef = useRef(null);
  const panelRef = useRef(null);
  const headingRef = useRef(null);
  const openerRef = useRef(null);

  const onJoin = isJoinPath(pathname) || !!(typeof window !== 'undefined' && window.__onJoinPage);
  const hostRedirect = !!(typeof window !== 'undefined' && window.__hostRedirectPending);
  const choice = getChoice();
  const bannerOpen = !choice && !onJoin && !hostRedirect;

  const openPanel = useCallback((opener) => {
    openerRef.current = opener || null;
    const current = getChoice();
    setDraft(current ? { ...current } : ALL_OFF);
    setPanelOpen(true);
  }, []);

  useEffect(() => {
    const onOpen = () => openPanel(getSettingsOpener());
    const onChange = () => setTick((t) => t + 1);
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpen);
    window.addEventListener(CONSENT_CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener(OPEN_SETTINGS_EVENT, onOpen);
      window.removeEventListener(CONSENT_CHANGE_EVENT, onChange);
    };
  }, [openPanel]);

  // Keep footers reachable: reserve room for the banner while it is shown.
  useLayoutEffect(() => {
    if (!bannerOpen) return undefined;
    const root = document.documentElement;
    const update = () => {
      const h = bannerRef.current ? bannerRef.current.offsetHeight : 0;
      root.style.setProperty('--ivori-consent-offset', `${h}px`);
    };
    root.classList.add('ivori-consent-open');
    update();
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      root.classList.remove('ivori-consent-open');
      root.style.removeProperty('--ivori-consent-offset');
    };
  }, [bannerOpen]);

  useEffect(() => {
    if (panelOpen && headingRef.current) headingRef.current.focus();
  }, [panelOpen]);

  const closePanel = () => {
    setPanelOpen(false);
    const opener = openerRef.current;
    openerRef.current = null;
    clearSettingsOpener();
    if (opener && typeof opener.focus === 'function' && document.body.contains(opener)) {
      opener.focus();
    }
  };

  const choose = (next) => {
    saveChoice(next);
    setPanelOpen(false);
    openerRef.current = null;
    clearSettingsOpener();
    setTick((t) => t + 1);
    // The banner is about to leave the page: do not strand focus on it.
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
  };

  const onPanelKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closePanel();
      return;
    }
    if (e.key !== 'Tab' || !panelRef.current) return;
    // Skip anything inside a closed <details> (other than its own summary).
    const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter((n) => {
      const closed = n.closest('details:not([open])');
      return !closed || (n.tagName === 'SUMMARY' && n.parentElement === closed);
    });
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === headingRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!bannerOpen && !panelOpen) return null;

  return (
    <>
      {bannerOpen && (
        <section className="ivori-consent-banner" aria-label="Cookie choices" ref={bannerRef}>
          <h2 className="ivori-consent-title">Cookies on this site</h2>
          <p>
            With your permission, we and our partners Google, Microsoft and Shown.io would like to set cookies to
            measure how the site is used, record visits to fix problems, measure our adverts, and show our adverts
            to people who have visited this site. These are off unless you turn them on, and you can change your
            choice in Cookie settings. If you are under 13, ask a parent or guardian before you accept.
          </p>
          <div className="ivori-consent-actions">
            <button type="button" className="ivori-consent-btn" onClick={() => choose(ALL_ON)}>Accept all</button>
            <button type="button" className="ivori-consent-btn" onClick={() => choose(ALL_OFF)}>Reject all</button>
            <button type="button" className="ivori-consent-btn" onClick={(e) => openPanel(e.currentTarget)}>Choose cookies</button>
          </div>
          <p className="ivori-consent-policy"><PolicyLink /></p>
        </section>
      )}
      {panelOpen && (
        <>
          <div className="ivori-consent-backdrop" onClick={closePanel} aria-hidden="true" />
          <div
            className="ivori-consent-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ivori-consent-panel-title"
            ref={panelRef}
            onKeyDown={onPanelKeyDown}
          >
            <h2 id="ivori-consent-panel-title" className="ivori-consent-title" tabIndex={-1} ref={headingRef}>Cookie settings</h2>
            <p>Choose which optional cookies we may use. Essential storage is always on because the site needs it to work.</p>

            <section className="ivori-consent-section" aria-labelledby="ivori-consent-essential">
              <h3 id="ivori-consent-essential">Essential <span className="ivori-consent-muted">(always on)</span></h3>
              <p>Keeps you signed in and remembers your settings. It is stored in your browser, not shared with advertisers.</p>
              <details>
                <summary>What is stored</summary>
                <EssentialTable />
              </details>
            </section>

            {OPTIONAL.map((cat) => (
              <section className="ivori-consent-section" key={cat.id} aria-labelledby={`ivori-consent-h-${cat.id}`}>
                <h3 id={`ivori-consent-h-${cat.id}`}>
                  <label className="ivori-consent-toggle">
                    <input
                      type="checkbox"
                      checked={draft[cat.id] === true}
                      onChange={(e) => setDraft({ ...draft, [cat.id]: e.target.checked })}
                    />
                    <span>{cat.title}</span>
                  </label>
                </h3>
                <p>{cat.purpose}</p>
                <details>
                  <summary>Providers and cookies</summary>
                  <ProviderTables category={cat.id} />
                </details>
              </section>
            ))}

            <p className="ivori-consent-policy"><PolicyLink onClick={closePanel} /></p>

            <div className="ivori-consent-actions">
              <button type="button" className="ivori-consent-btn" onClick={() => choose(ALL_ON)}>Accept all</button>
              <button type="button" className="ivori-consent-btn" onClick={() => choose(ALL_OFF)}>Reject all</button>
              <button type="button" className="ivori-consent-btn" onClick={() => choose(draft)}>Save choices</button>
              <button type="button" className="ivori-consent-btn" onClick={closePanel}>Close</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
